import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { holdingsService } from '../services/holdingsService';
import { performanceService } from '../services/performanceService';
import { lettersService } from '../services/lettersService';
import { LEGACY_LETTERS, loadStaticPerformance } from '../data/legacyData';

const FALLBACK_LETTERS = LEGACY_LETTERS.map((letter, index) => ({
  id: `static-${index}`,
  ...letter,
}));

const FundDataContext = createContext(null);

export function useFundData() {
  const ctx = useContext(FundDataContext);
  if (!ctx) {
    throw new Error('useFundData must be used within FundDataProvider');
  }
  return ctx;
}

const isPermissionError = (err) =>
  err?.code === 'permission-denied' ||
  String(err?.message || '').includes('insufficient permissions');

/**
 * Single set of Firestore listeners for investor-facing data.
 * Subscriptions persist for the authenticated session to avoid listener churn.
 * Collections without Firestore rules yet fall back to bundled static data.
 */
export function FundDataProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [holdings, setHoldings] = useState([]);
  const [fundMeta, setFundMeta] = useState({ cash: 0 });
  const [performanceRows, setPerformanceRows] = useState([]);
  const [lettersRows, setLettersRows] = useState([]);
  const [staticPerformance, setStaticPerformance] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setHoldings([]);
      setFundMeta({ cash: 0 });
      setPerformanceRows([]);
      setLettersRows([]);
      setStaticPerformance(null);
      setReady(false);
      return undefined;
    }

    let cancelled = false;
    const unsubs = [];

    // Preload bundled fallbacks immediately so pages render fast even when
    // Firestore collections are empty or blocked by security rules.
    loadStaticPerformance()
      .then((rows) => {
        if (!cancelled) setStaticPerformance(rows);
      })
      .catch(() => {
        if (!cancelled) setStaticPerformance([]);
      });

    // Three gate subscriptions (holdings, performance, letters).
    // Fund meta is optional and never blocks ready.
    let pending = 3;
    const markReady = () => {
      pending -= 1;
      if (!cancelled && pending <= 0) setReady(true);
    };

    // Safety net: never spin forever if a listener hangs.
    const safetyTimer = setTimeout(() => {
      if (!cancelled) setReady(true);
    }, 1200);

    const loadPerformanceFallback = async () => {
      try {
        const rows = await loadStaticPerformance();
        if (!cancelled) setStaticPerformance(rows);
      } catch {
        if (!cancelled) setStaticPerformance([]);
      }
    };

    const logSubError = (label, err) => {
      if (isPermissionError(err)) {
        console.warn(
          `[FundData] ${label}: Firestore read denied — using fallback. ` +
            'Update security rules when ready to store this data in Firestore.'
        );
      } else {
        console.error(`${label} subscription error:`, err);
      }
    };

    unsubs.push(
      holdingsService.subscribeToHoldings(
        (data) => {
          setHoldings(data);
          markReady();
        },
        (err) => {
          logSubError('Holdings', err);
          markReady();
        }
      )
    );

    unsubs.push(
      holdingsService.subscribeToFundMeta(
        (meta) => setFundMeta(meta || { cash: 0 }),
        (err) => {
          logSubError('Fund meta', err);
          setFundMeta({ cash: 0 });
        }
      )
    );

    unsubs.push(
      performanceService.subscribeToPerformance(
        async (rows) => {
          setPerformanceRows(rows);
          if (rows.length === 0) await loadPerformanceFallback();
          else setStaticPerformance(null);
          markReady();
        },
        async (err) => {
          logSubError('Performance', err);
          await loadPerformanceFallback();
          markReady();
        }
      )
    );

    unsubs.push(
      lettersService.subscribeToLetters(
        (data) => {
          setLettersRows(data);
          markReady();
        },
        (err) => {
          logSubError('Letters', err);
          markReady();
        }
      )
    );

    return () => {
      cancelled = true;
      clearTimeout(safetyTimer);
      unsubs.forEach((unsub) => unsub());
    };
  }, [isAuthenticated]);

  const performance = useMemo(() => {
    if (performanceRows.length > 0) return performanceRows;
    return staticPerformance || [];
  }, [performanceRows, staticPerformance]);

  const letters = useMemo(() => {
    if (lettersRows.length > 0) return lettersRows;
    return FALLBACK_LETTERS;
  }, [lettersRows]);

  const value = useMemo(
    () => ({ holdings, fundMeta, performance, letters, ready }),
    [holdings, fundMeta, performance, letters, ready]
  );

  return <FundDataContext.Provider value={value}>{children}</FundDataContext.Provider>;
}
